import { useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector } from 'statux'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '@reach/tabs'

import DashboardSettingsForm from './DashboardSettingsForm/DashboardSettingsForm'
import DashboardSettingsInputUpload
    from './DashboardSettingsInputUpload/DashboardSettingsInputUpload'

import Alert from '../../../components/Alert/Alert'
import RichText from '../../../components/RichText/RichText'
import TextArea from '../../../components/TextArea/TextArea'

import authProvider from '../../../utils/auth-provider'
import { getCssVar } from '../../../utils/css'
import { graphqlClient, gql } from '../../../utils/gql'

export default function DashboardSettings (props = {
    heading: null,
    richText: null,
    tabs: null,
    advancedMembersApplyAlerts: null
}) {
    const isAdmin = authProvider.getDefaultRole() === 'admin'

    const isEbpcJudge = authProvider.getDefaultRole() === 'ebpc_judge'

    const isEbpcReviewer = authProvider.getDefaultRole() === 'ebpc_reviewer'

    const isEbpcRole = isEbpcJudge || isEbpcReviewer

    if (isAdmin)
        return window.location.assign(`/admin/#/users/${
            (authProvider.getSession() || {}).sub}`)

    const user = useSelector('user')

    const history = useHistory()

    const params = useParams()

    const tabs = (props.tabs || [])
        .filter(obj => !(obj.userRoles || []).length
            || obj.userRoles.indexOf(authProvider.getDefaultRole())
                >= 0)

    const textareaMaxLength = 500

    const cachedProfile = (history.location.state || {}).profile

    const [profile, setProfile] = useState(cachedProfile
            && (cachedProfile.user || {}).id
                === (user || {}).id
        ? cachedProfile
        : null)

    let { advancedMembersApplyEndDate } = useSelector('options')

    if (advancedMembersApplyEndDate)
        advancedMembersApplyEndDate = new Date(advancedMembersApplyEndDate)

    const [advancedMembersApplyAlert, setAdvancedMembersApplyAlert]
        = useState(null)

    useEffect(() => {
        if (!(props.advancedMembersApplyAlerts || []).length
                || !profile
                || profile.advanced_membership
                || profile.role !== 'member'
                || (advancedMembersApplyEndDate
                    && advancedMembersApplyEndDate.getTime()
                        < (new Date()).getTime()))
            return

        let alert

        try {
            alert = JSON.parse(JSON.stringify(
                (props.advancedMembersApplyAlerts || [])
                    .filter(obj => (!profile.advanced_membership_application
                            && obj.type === 'advanced-membership-apply')
                        || ((profile.advanced_membership_application || {})
                            .complete === false
                                && obj.type === 'advanced-membership-incomplete')
                        || ((profile.advanced_membership_application || {}).complete
                            && obj.type === 'advanced-membership-complete'))[0]))
        }
        catch (e) {}

        if (!(alert || {}).type)
            return

        alert.className = `alert-dashboard-settings-${alert.type}`

        alert.dismissible = false

        alert.fluid = true

        alert.type = alert.type === 'advanced-membership-incomplete'
            ? 'error'
            : null

        alert.role = 'status'

        alert.icon = alert.type === 'error'
            ? 'icon-schedule'
            : 'icon-visibility'

        alert.iconClassName = 'ratio-square round'

        console.log(alert)

        if (JSON.stringify(alert)
                !== JSON.stringify(advancedMembersApplyAlert))
            setAdvancedMembersApplyAlert(alert)
    }, [profile])

    useEffect(async () => {
        const queryProfile = await graphqlClient
            .request(gql`
                query {
                    profiles (
                        limit: 1,
                        where: {
                            user: { id: { _eq: "${authProvider.getIdentity().id}" } }
                        }
                    ) {
                        id
                        email
                        role
                        approved
                        first_name
                        last_name
                        bio
                        title
                        linkedin
                        type
                        details
                        company
                        company_logo
                        company_bio
                        company_website
                        company_goals
                        company_goals_public
                        company_materials
                        advanced_membership
                        advanced_membership_application
                        user {
                            id
                            avatar_url
                        }
                    }
                }`)
            .then(obj => (obj.profiles || [])[0])
            .catch((err) => {
                console.warn(err)
                return false
            })

        if ((((queryProfile || {}).user || {}).avatar_url || '')
            .match(/\.gravatar\.com/))
            queryProfile.user.avatar_url = null

        setProfile(queryProfile)

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                profile: queryProfile
            }))
    }, [user])

    if (!tabs.length)
        return null

    const getActiveTab = () => tabs
        .filter((obj, idx) => params.tab === obj.slug)[0]
            || tabs[0]

    const getActiveIndex = () => tabs.indexOf(getActiveTab())

    const onTabChange = (e) => {
        const tab = tabs[e]

        if (!tab.path
                || tab.path === history.location.pathname
                    .replace(/\/$/, ''))
            return

        history.push(tab.path, { profile })
    }

    const renderPersonalType = () => profile
        && profile.role
        && profile.type
        && (
            <div className="pad-5-t">
                <h2
                    className="heading-6 font-weight-medium"
                    style={{ marginBottom: '5px' }}
                >
                    {profile.role === 'member'
                        ? 'Stage'
                        : 'Advisor-type'}
                </h2>
                <p className="body-4">
                    <span
                        className={profile.role === 'advisor'
                            ? 'chip-secondary'
                            : null}
                    >
                        {profile.type}
                    </span>
                    <br />
                    <span
                        className="display-block"
                        style={{
                            color: getCssVar('--slate'),
                            marginTop: '5px'
                        }}
                    >
                        You cannot modify this setting.
                    </span>
                </p>
            </div>)

    const renderPersonalFormInputs = (id) => (profile || isEbpcRole)
        && id
        && (
            <>
                <DashboardSettingsInputUpload
                    id={`${id}-avatar_url`}
                    name="avatar_url"
                    label="Upload a profile picture:"
                    text="Help others recognize you on Catapult"
                    filenamePrefix="avatar"
                    type="image"
                    preview={isEbpcRole
                        ? (authProvider.getIdentity() || {}).avatar_url
                        : ((profile || {}).user || {}).avatar_url || user.avatar_url}
                    previewFallback={authProvider.getInitials()}
                />
                <h2 className="body-3 font-weight-medium margin-1-b">Personal information</h2>
                {isEbpcRole
                    && (
                        <>
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-display_name`}
                                    name="display_name"
                                    type="text"
                                    autoComplete="name"
                                    autoCapitalize="on"
                                    spellCheck="false"
                                    pattern=".+"
                                    placeholder=" "
                                    defaultValue={user.display_name}
                                    required
                                />
                                <label
                                    htmlFor={`${id}-display_name`}
                                >
                                    Full name*
                                </label>
                                <p
                                    id={`${id}-display_name-invalid`}
                                    role="alert"
                                >
                                    A name is required
                                </p>
                            </div>
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-email`}
                                    name="email"
                                    type="email"
                                    autoComplete="on"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    pattern="[a-zA-Z0-9\._%\+\-]+@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,}$"
                                    placeholder=" "
                                    defaultValue={user.email}
                                    required
                                />
                                <label
                                    htmlFor={`${id}-email`}
                                >
                                    Email address*
                                </label>
                                <p
                                    id={`${id}-email-invalid`}
                                    role="alert"
                                >
                                    A valid email address is required
                                </p>
                            </div>
                        </>
                    )}
                {profile
                    && (
                        <>
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-first_name`}
                                    name="first_name"
                                    type="text"
                                    autoComplete="given-name"
                                    autoCapitalize="on"
                                    spellCheck="false"
                                    pattern=".{2,}$"
                                    placeholder=" "
                                    defaultValue={profile.first_name}
                                    required
                                />
                                <label
                                    htmlFor={`${id}-first_name`}
                                >
                                    First name*
                                </label>
                                <p
                                    id={`${id}-first_name-invalid`}
                                    role="alert"
                                >
                                    A first name is required
                                </p>
                            </div>
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-last_name`}
                                    name="last_name"
                                    type="text"
                                    autoComplete="family-name"
                                    autoCapitalize="on"
                                    spellCheck="false"
                                    pattern=".+"
                                    placeholder=" "
                                    defaultValue={profile.last_name}
                                    required
                                />
                                <label
                                    htmlFor={`${id}-last_name`}
                                >
                                    Last name*
                                </label>
                                <p
                                    id={`${id}-last_name-invalid`}
                                    role="alert"
                                >
                                    A last name is required
                                </p>
                            </div>
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-email`}
                                    name="email"
                                    type="email"
                                    autoComplete="on"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    pattern="[a-zA-Z0-9\._%\+\-]+@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,}$"
                                    placeholder=" "
                                    defaultValue={profile.email}
                                    required
                                />
                                <label
                                    htmlFor={`${id}-email`}
                                >
                                    Email address*
                                </label>
                                <p
                                    id={`${id}-email-invalid`}
                                    role="alert"
                                >
                                    A valid email address is required
                                </p>
                            </div>
                            {profile.approved
                                && (
                                    <div className="form-field-floating margin-3-b">
                                        <input
                                            id={`${id}-title`}
                                            name="title"
                                            type="text"
                                            autoComplete="organization-title"
                                            autoCapitalize="on"
                                            spellCheck="false"
                                            pattern=".+"
                                            placeholder=" "
                                            defaultValue={profile.title}
                                            required
                                        />
                                        <label
                                            htmlFor={`${id}-title`}
                                        >
                                            Title*
                                        </label>
                                        <p
                                            id={`${id}-title-invalid`}
                                            role="alert"
                                        >
                                            A professional title is required
                                        </p>
                                    </div>)}
                            <div className="form-field-floating margin-3-b">
                                <input
                                    id={`${id}-linkedin`}
                                    name="linkedin"
                                    type="url"
                                    autoComplete="on"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    pattern="^(https*:\/\/)*(www\.)*linkedin\.com\/.+"
                                    data-url-scheme="https"
                                    defaultValue={profile.linkedin || null}
                                    placeholder=" "
                                />
                                <label
                                    htmlFor={`${id}-linkedin`}
                                >
                                    LinkedIn profile URL
                                </label>
                                <p
                                    id={`${id}-linked-invalid`}
                                    role="alert"
                                >
                                    A valid linkedin.com URL is required and must start with https://
                                </p>
                            </div>
                            {profile.approved
                                && (
                                    <div className="form-field margin-3-b">
                                        <label
                                            htmlFor={`${id}-bio`}
                                            className="font-weight-medium"
                                        >
                                            Bio*
                                        </label>
                                        <TextArea
                                            id={`${id}-bio`}
                                            className="margin-1-t"
                                            name="bio"
                                            autoCapitalize="sentences"
                                            autoComplete="off"
                                            spellCheck="true"
                                            minLength="1"
                                            maxLength={textareaMaxLength}
                                            placeholder=" "
                                            defaultValue={profile.bio}
                                            required
                                        />
                                        <p
                                            id={`${id}-bio-invalid`}
                                            role="alert"
                                        >
                                            A bio is required
                                        </p>
                                    </div>)}
                        </>)}
            </>)

    const renderCompanyFormInputs = (id) => profile
        && id
        && (
            <>
                <DashboardSettingsInputUpload
                    id={`${id}-company_logo`}
                    name="company_logo"
                    label="Upload a logo:"
                    text="Help others recognize your company on Catapult"
                    filenamePrefix="company-logo"
                    type="image"
                    preview={profile.company_logo}
                    previewFallback={(
                        <svg
                            className="icon-extension flex-none"
                            width="64"
                            height="64"
                        >
                            <use xlinkHref="#icon-extension" />
                        </svg>
                    )}
                />
                <div className="form-field-floating margin-3-b">
                    <input
                        id={`${id}-company`}
                        name="company"
                        type="text"
                        autoComplete="organization"
                        autoCapitalize="on"
                        spellCheck="false"
                        pattern=".{2,}$"
                        placeholder=" "
                        defaultValue={profile.company}
                        required
                    />
                    <label
                        htmlFor={`${id}-company`}
                    >
                        Name*
                    </label>
                    <p
                        id={`${id}-company-invalid`}
                        role="alert"
                    >
                        A company name is required
                    </p>
                </div>
                <div className="form-field-floating margin-3-b">
                    <input
                        id={`${id}-company-website`}
                        name="company_website"
                        type="url"
                        autoComplete="on"
                        autoCapitalize="none"
                        spellCheck="false"
                        pattern="^(https*:\/\/)*(www\.)*.+"
                        data-url-scheme="https"
                        defaultValue={profile.company_website || null}
                        placeholder=" "
                    />
                    <label
                        htmlFor={`${id}-company-website`}
                    >
                        Website
                    </label>
                    <p
                        id={`${id}-company-website`}
                        role="alert"
                    >
                        A valid URL is required and must start with https://
                    </p>
                </div>
                <div className="form-field margin-3-b">
                    <label
                        htmlFor={`${id}-company-bio`}
                        className="font-weight-medium"
                    >
                        About my company*
                    </label>
                    <p
                        className="body-3"
                        style={{ color: getCssVar('--slate') }}
                    >
                        Please share a brief description of the organization you
                        work with, highlighting any details that might be relevant
                        to the {profile.role === 'advisor'
                            ? 'support you’ll provide Catapult entrepreneurs.'
                            : 'Catapult advisors providing you support.'}
                    </p>
                    <TextArea
                        id={`${id}-company-bio`}
                        className="margin-1-t"
                        name="company_bio"
                        autoCapitalize="sentences"
                        autoComplete="off"
                        spellCheck="true"
                        minLength="1"
                        maxLength={textareaMaxLength}
                        placeholder=" "
                        defaultValue={profile.company_bio}
                        required
                    />
                    <p
                        id={`${id}-company-bio-invalid`}
                        role="alert"
                    >
                        A bio is required
                    </p>
                </div>
                {profile.role === 'member'
                    && (
                        <>
                            <div className="form-field margin-3-b">
                                <label
                                    htmlFor={`${id}-company-goals`}
                                    className="font-weight-medium"
                                >
                                    Catapult goals
                                </label>
                                <TextArea
                                    id={`${id}-company-goals`}
                                    className="margin-1-t"
                                    name="company_goals"
                                    autoCapitalize="sentences"
                                    autoComplete="off"
                                    spellCheck="true"
                                    minLength="0"
                                    maxLength={textareaMaxLength}
                                    placeholder=" "
                                    defaultValue={profile.company_goals}
                                />
                            </div>
                            <div className="form-field display-flex margin-3-b">
                                <input
                                    id={`${id}-company-goals-public`}
                                    name="company_goals_public"
                                    type="checkbox"
                                    defaultChecked={profile.company_goals_public}
                                />
                                <div className="margin-l">
                                    <label
                                        htmlFor={`${id}-company-goals-public`}
                                        className="font-weight-medium"
                                        style={{ marginTop: '-1.5px' }}
                                    >
                                        Publicly display my response to
                                        "Catapult goals".
                                    </label>
                                    <p
                                        className="body-3"
                                        style={{ color: getCssVar('--slate') }}
                                    >
                                        Enabling this setting allows other
                                        entrepreneurs to read about your goals
                                        when viewing your profile. Even if this
                                        setting is disabled, advisors can see
                                        this information, helping them better
                                        respond to your needs.
                                    </p>
                                </div>
                            </div>
                        </>)}
            </>)

    return (
        <div className="dashboard-settings pad-3-t pad-1-b pad-4-t-lg-up pad-4-b-lg-up">
            <div className="container">
                <div className="row margin-3-b">
                    <div className="col-12 col-8-xl-up">
                        <h1
                            className="heading-2"
                            style={{
                                marginBottom: '5px'
                            }}
                        >
                            {props.heading || 'Settings'}
                        </h1>
                        <RichText
                            className="body-3"
                            html={props.richText}
                        />
                    </div>
                </div>
                <div className="row flex-justify-content-center">
                    <div className="col-12 col-10-lg-up">
                        {advancedMembersApplyAlert
                            && (
                                <div className="dashboard-settings-alert card margin-3-b">
                                    <Alert {...advancedMembersApplyAlert} />
                                </div>)}
                        <div className="card card-offscreen-x-lg-down">
                            <Tabs
                                className="tabs"
                                defaultIndex={getActiveIndex()}
                                index={getActiveIndex()}
                                onChange={onTabChange}
                            >
                                <div className="tablist-container tablist-container-horizontal margin-5-b">
                                    <TabList className="tablist-nav">
                                        {tabs
                                            .map((obj, idx) => (
                                                <Tab
                                                    key={`dashboard-settings-tab-${idx}`}
                                                    className="btn-unstyled"
                                                >
                                                    <span className="display">{obj.title}</span>
                                                </Tab>
                                            ))}
                                    </TabList>
                                </div>
                                <TabPanels>
                                    {tabs
                                        .map((obj, idx) => (
                                            <TabPanel
                                                key={`dashboard-settings-tabpanel-${idx}`}
                                            >
                                                <div className="row flex-justify-content-center">
                                                    <div className="col-12">
                                                        {(obj.slug === 'personal'
                                                                || obj.slug === 'company')
                                                            && (profile || isEbpcRole)
                                                            && (
                                                                <>
                                                                    <DashboardSettingsForm
                                                                        id={`${obj.slug}-form`}
                                                                        profile={profile}
                                                                        setProfile={setProfile}
                                                                        successText={obj.successText}
                                                                        customValidations={[{
                                                                            id: `${obj.slug}-form-${
                                                                                obj.slug === 'personal'
                                                                                    ? 'avatar_url'
                                                                                    : 'company_logo'}-file`,
                                                                            message: 'Maximum file size is 2MB',
                                                                            valid: (e) => {
                                                                                const file = e.target
                                                                                    && (e.target.files || [])[0]

                                                                                return !file
                                                                                    || file.size <= 1048576 * 2
                                                                            }
                                                                        }]}
                                                                    >
                                                                        {obj.slug === 'personal'
                                                                            ? renderPersonalFormInputs(
                                                                                `${obj.slug}-form`)
                                                                            : renderCompanyFormInputs(
                                                                                `${obj.slug}-form`)}
                                                                    </DashboardSettingsForm>
                                                                    {obj.slug === 'personal'
                                                                        && renderPersonalType()}
                                                                </>)}
                                                        {(obj.slug === 'password'
                                                                || (obj.slug === 'materials'
                                                                    && profile))
                                                            && (
                                                                <DashboardSettingsForm
                                                                    id={`${obj.slug}-form`}
                                                                    successText={obj.successText}
                                                                    changePassword={obj.slug === 'password'}
                                                                    materials={obj.slug === 'materials'}
                                                                    profile={obj.slug === 'materials'
                                                                        && profile}
                                                                    setProfile={obj.slug === 'materials'
                                                                        && setProfile}
                                                                />)}
                                                    </div>
                                                </div>
                                            </TabPanel>
                                        ))}
                                </TabPanels>
                            </Tabs>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
